import { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import classnames from 'classnames'
import { Formik, FormikProps } from 'formik'
import * as Yup from 'yup'
import { MixedSchema } from 'yup'

import { IsPurchaseDateValidForEmployeeResponse, ProductIds, isPremiumPurchaseDateValidForEmployee } from 'core'
import { ReviewerAction } from 'core/application/docReviewDashboard/expenses/reviewerAction'
import getRecurringExpenseDetailsForReview, {
  RecurringExpenseDetailsForReview,
} from 'core/application/expenses/getRecurringExpenseDetailsForReview'
import reviewPremium, { ReviewPremiumRequest } from 'core/application/expenses/reviewPremium'
import skipRecurringExpenseForReview from 'core/application/expenses/skipRecurringExpenseForReview'
import { RecurringExpenseFrequency } from 'core/domain/expenses/ReccuringExpenseFrequency'
import { RecurringReviewSectionStatus } from 'core/domain/expenses/recurringReviewSectionStatus'
import { Product } from 'core/domain/product/Product'

import Card from 'common/components/dataDisplay/CustomCard'

import useCommandRequest from 'common/hooks/useCommandRequest'
import useQueryRequest from 'common/hooks/useQueryRequest'

import {
  Button,
  Col,
  DatePicker,
  DefaultSelect,
  FormikSelectWithChildrenField,
  FormikTextAreaField,
  HtmlEditor,
  Icon,
  InputNumber,
  Option,
  Row,
  Skeleton,
  TextArea,
  Tooltip,
  filterByTooltipValue,
  filterByValue,
} from 'common/components'
import { parseZoneFormatV2, toDateWithoutTimeZoneV2 } from 'common/dateFormatters'

import { ActionIcon } from '../../common/ActionIcon'
import DocumentComponent from '../../common/DocumentComponet'
import UserInfo from '../../common/userInfo'
import PriorPremiums from './priorPremiums'

import './styles.less'

type ReviewPremiumProps = {
  recurringExpenseId: string
  onPremiumReviewed?: (recurringExpenseId: string) => void
  onPremiumSkipped?: (recurringExpenseId: string) => void
  onPriorPremiumClick?: (recurringExpenseId: string) => void
  refreshAfterReview?: boolean
}
const ReviewPremiumForm = (props: ReviewPremiumProps) => {
  const { t: translate } = useTranslation()

  const [premium, isPremiumLoading, sendGetPremium] = useQueryRequest<RecurringExpenseDetailsForReview>(null)
  const [showViewHistory, setShowViewHistory] = useState(false)
  const [initialValues, setInitialValues] = useState<FormData | null>()
  const [sendReviewPremiumCommand, isReviewPremiumCommandLoading] = useCommandRequest<string>()
  const [sendSkipRecurringExpenseFoReviewCommand, isSkipRecurringExpenseFoReviewCommandLoading] = useCommandRequest()
  const [, , sendIsPurchaseDateValidForEmployee] = useQueryRequest<IsPurchaseDateValidForEmployeeResponse>(null)
  const [overrideStartDateValidResponse, setOverrideStartDateValidResponse] =
    useState<IsPurchaseDateValidForEmployeeResponse>(IsPurchaseDateValidForEmployeeResponse.Correct)
  const [overrideEndDateValidResponse, setOverrideEndDateValidResponse] =
    useState<IsPurchaseDateValidForEmployeeResponse>(IsPurchaseDateValidForEmployeeResponse.Correct)

  useEffect(() => {
    ;(async () => {
      if (props.recurringExpenseId) {
        await sendGetPremium(() => getRecurringExpenseDetailsForReview(props.recurringExpenseId))
      }
    })()
  }, [props.recurringExpenseId])

  useEffect(() => {
    if (premium) {
      setInitialValues({
        ...premium,
        overrideStartDateSelect: premium.lastReview
          ? premium.lastReview.overrideStartDate
            ? parseZoneFormatV2(premium.lastReview.overrideStartDate)
            : premium.lastReview.startDateReviewStatus
          : undefined,
        overrideStartDate: premium.lastReview?.overrideStartDate,
        overrideEndDateSelect: premium.lastReview
          ? premium.lastReview.overrideEndDate
            ? parseZoneFormatV2(premium.lastReview.overrideEndDate)
            : premium.lastReview.endDateReviewStatus
          : undefined,
        overrideEndDate: premium.lastReview?.overrideEndDate,
        overrideAmountSelect: premium.lastReview
          ? premium.lastReview.overrideAmount
            ? premium.lastReview.overrideAmount
            : premium.lastReview.amountReviewStatus
          : undefined,
        overrideAmount: premium.lastReview?.overrideAmount,
        overrideExpenseTypeIdSelect: premium.lastReview
          ? premium.lastReview.overrideExpenseTypeId
            ? premium.availableExpenseTypes.find((x) => x.id === premium.lastReview.overrideExpenseTypeId)?.name
            : premium.lastReview.expenseTypeReviewStatus
          : undefined,
        overrideExpenseTypeId: premium.lastReview?.overrideExpenseTypeId,
        overrideFrequencySelect: premium.lastReview
          ? premium.lastReview.overrideFrequency ||
            premium.lastReview.overrideFrequency === RecurringExpenseFrequency.Monthly
            ? premium.availableFrequencies.find((x) => x.id == premium.lastReview.overrideFrequency)?.name
            : premium.lastReview.frequencyReviewStatus
          : undefined,
        overrideFrequency: premium.lastReview?.overrideFrequency,
        overrideNameSelect: premium.lastReview
          ? premium.lastReview.overrideName
            ? premium.lastReview.overrideName
            : premium.lastReview.nameReviewStatus
          : undefined,
        overrideName: premium.lastReview?.overrideName,
        messageToReviewer: premium.lastReview?.messageToReviewer,
      })
      setOverrideStartDateValidResponse(IsPurchaseDateValidForEmployeeResponse.Correct)
    }
  }, [premium])

  const saveReview = async (formik: FormikProps<FormData>) => {
    const request: ReviewPremiumRequest = {
      recurringExpenseId: premium.recurringExpenseId,
      overrideExpenseTypeId:
        formik.values.overrideExpenseTypeId !== -1 && formik.values.overrideExpenseTypeId !== -2
          ? formik.values.overrideExpenseTypeId
          : null,
      expenseTypeReviewStatus: getRecurringReviewSectionStatus(formik.values.overrideExpenseTypeIdSelect),
      overrideAmount: formik.values.overrideAmount,
      amountReviewStatus:
        formik.values.overrideExpenseTypeId === -2
          ? RecurringReviewSectionStatus.Verified
          : getRecurringReviewSectionStatus(formik.values.overrideAmountSelect),
      overrideStartDate: formik.values.overrideStartDate,
      startDateReviewStatus:
        formik.values.overrideExpenseTypeId === -2
          ? RecurringReviewSectionStatus.Verified
          : getRecurringReviewSectionStatus(formik.values.overrideStartDateSelect),
      overrideFrequency: formik.values.overrideFrequency,
      frequencyReviewStatus:
        formik.values.overrideExpenseTypeId === -2
          ? RecurringReviewSectionStatus.Verified
          : getRecurringReviewSectionStatus(formik.values.overrideFrequencySelect),
      messageToEmployee: formik.values.messageToEmployee,
      messageToReviewer: formik.values.messageToReviewer,
      overrideEndDate: formik.values.overrideEndDate,
      endDateReviewStatus:
        formik.values.overrideExpenseTypeId === -2
          ? RecurringReviewSectionStatus.Verified
          : getRecurringReviewSectionStatus(formik.values.overrideEndDateSelect),
      ineligibleExpenseType: formik.values.overrideExpenseTypeId === -1,
      overrideName: formik.values.overrideName,
      nameReviewStatus:
        formik.values.overrideExpenseTypeId === -2
          ? RecurringReviewSectionStatus.Verified
          : getRecurringReviewSectionStatus(formik.values.overrideNameSelect),
      isDuplicate: formik.values.overrideExpenseTypeId === -2,
    }

    const result = await sendReviewPremiumCommand(() => reviewPremium(request))

    if (result.completed) {
      if (props.refreshAfterReview) {
        await sendGetPremium(() => getRecurringExpenseDetailsForReview(premium.recurringExpenseId))
      }
      window.scrollTo(0, 0)
      formik.resetForm()

      if (props.onPremiumReviewed) {
        props.onPremiumReviewed(premium.recurringExpenseId)
      }
    }
  }

  const skipExpense = async (formik: FormikProps<FormData>) => {
    const result = await sendSkipRecurringExpenseFoReviewCommand(() =>
      skipRecurringExpenseForReview(premium.recurringExpenseId),
    )

    if (result.completed) {
      window.scrollTo(0, 0)
      formik.resetForm()

      if (props.onPremiumSkipped) {
        props.onPremiumSkipped(premium.recurringExpenseId)
      }
    }
  }

  const getMessageToEmployeeValue = (formik: FormikProps<FormData>) => {
    if (formik.values.messageToEmployeeTemplateSelect) {
      const messages = premium.availableCannedMessages.filter(
        (c) => c.id === formik.values.messageToEmployeeTemplateSelect,
      )
      return messages.length > 0 ? messages[0].message : ''
    }

    return ''
  }

  return (
    <Skeleton loading={isPremiumLoading}>
      {initialValues && (
        <Card
          title={`${translate('doc-review-dashboard.premiums.review-premium-title')} #${initialValues.expenseNumber}`}
        >
          <Row>
            <Col span={24} className="mt-4 mb-4">
              <UserInfo
                name={`${initialValues.companyUser.firstName} ${initialValues.companyUser.lastName}`}
                {...initialValues.companyUser}
              />
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <div className="flex__space_between ml-16">
                <div>
                  <span className="color-gray-6">{translate('doc-review-dashboard.expenses.plan-eligibility')}</span>
                  {initialValues.planEligibilities.map((item: any, index: number) => {
                    return (
                      <div key={index} className={item.isInPlanYear ? 'review_expense__benefit_info_font_bold' : ''}>
                        <span className={'mr-16'}>
                          {parseZoneFormatV2(item.startDate)} - {parseZoneFormatV2(item.endDate)}
                        </span>
                        {item.reimburseAnotherEmployeePlan ? (
                          <span>SP: YES</span>
                        ) : (
                          <span className={'color-red-6'}>SP: No</span>
                        )}
                      </div>
                    )
                  })}
                </div>
                <div>
                  <span className="color-gray-6">{translate('doc-review-dashboard.expenses.status')}</span>
                  <p>{initialValues.status.name}</p>
                </div>
                <div>
                  <span className="color-gray-6">{translate('doc-review-dashboard.expenses.review-history')}</span>
                  <p>{initialValues.reviewHistory ? initialValues.reviewHistory : 'new'}</p>
                  {showViewHistory && (
                    <p className="color-blue-6 cursor_pointer" onClick={() => {}}>
                      {translate('doc-review-dashboard.expenses.view-history-details')}
                    </p>
                  )}
                  <div>
                    <div>
                      <b>Original Submission Date:</b> {parseZoneFormatV2(initialValues.firstSubmittedOn)}
                    </div>

                    {initialValues.lastSubmittedAt && (
                      <div>
                        <b>Awaiting review:</b> {parseZoneFormatV2(initialValues.lastSubmittedAt)}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <span className="color-gray-6">{translate('doc-review-dashboard.expenses.product')}</span>
                  <p>
                    {initialValues.productId === ProductIds.IchraId ? (
                      <span className={'color-red-6'}>
                        <b>{initialValues.productName}</b>
                      </span>
                    ) : (
                      <b>{initialValues.productName}</b>
                    )}
                  </p>
                </div>
                {initialValues.productId !== ProductIds.Perks && (
                  <div className="mr-16">
                    <div className="color-gray-6">HSA Coverage:</div>
                    {initialValues.hsaCoverage.map((item, index) => (
                      <div key={index} className={'mr-0'}>
                        {item.year} - {item.coveredByHsa ? 'Yes' : 'No'}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Col>
          </Row>

          <Row>
            <Col span={17}>
              <Formik
                enableReinitialize={true}
                validationSchema={formValidator(translate)}
                initialValues={initialValues}
                onSubmit={() => {}}
              >
                {(formik: FormikProps<FormData>) => (
                  <form onSubmit={formik.handleSubmit} autoComplete="off">
                    <Row className="review_form_header">
                      <Col span={8}>
                        <div></div>
                      </Col>
                      <Col span={8}>
                        <div>
                          <h6>{translate('doc-review-dashboard.expenses.review-expense-form.participant')}</h6>
                        </div>
                      </Col>
                      <Col span={8}>
                        <div>
                          <h6>{translate('doc-review-dashboard.expenses.review-expense-form.reviewer')}</h6>
                        </div>
                      </Col>
                    </Row>

                    <PremiumFormItem
                      itemName={translate('doc-review-dashboard.expenses.review-expense-form.date-submitted')}
                      itemValue={parseZoneFormatV2(initialValues.lastSubmittedAt ?? initialValues.firstSubmittedOn)}
                    />

                    <PremiumFormItem
                      itemName={translate('doc-review-dashboard.expenses.review-expense-form.expense-id')}
                      itemValue={initialValues.expenseNumber}
                    />

                    <PremiumFormItem
                      itemName={translate('doc-review-dashboard.premiums.review-premium-form.premium-is-for')}
                      itemValue={
                        initialValues.familyMember
                          ? `${initialValues.familyMember.name}`
                          : `${initialValues.companyUser.firstName} ${initialValues.companyUser.lastName}`
                      }
                    />

                    <PremiumFormItemSelect
                      itemDisplayName={'Name'}
                      itemDisplayValue={initialValues.policyName}
                      inputName={'overrideNameSelect'}
                      inputValue={
                        formik.values?.overrideNameSelect != undefined ? [formik.values?.overrideNameSelect] : undefined
                      }
                      disabled={!initialValues.canReview}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault()
                          if (formik.values?.overrideName) {
                            formik.setFieldValue('overrideNameSelect', formik.values?.overrideName)
                          }
                        }
                      }}
                      customOption={
                        <TextArea
                          name="overrideName"
                          onChange={(value) => {
                            formik.setFieldValue('overrideNameSelect', value.currentTarget.value)
                            formik.setFieldValue('overrideName', value.currentTarget.value)
                          }}
                        />
                      }
                      actionIcon={getActionIcon(formik.values?.overrideNameSelect)}
                    />

                    <PremiumFormItemSelect
                      itemDisplayName={'Start Date'}
                      itemDisplayValue={parseZoneFormatV2(initialValues.startDate)}
                      inputName={'overrideStartDateSelect'}
                      inputValue={
                        formik.values?.overrideStartDateSelect != undefined
                          ? [formik.values?.overrideStartDateSelect]
                          : undefined
                      }
                      disabled={!initialValues.canReview}
                      actionComponentError={
                        overrideStartDateValidResponse !== IsPurchaseDateValidForEmployeeResponse.Correct
                          ? `Value is outside user's eligibility dates.`
                          : null
                      }
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault()
                          if (formik.values?.overrideStartDate) {
                            formik.setFieldValue('overrideStartDateSelect', formik.values?.overrideStartDate)
                          }
                        }
                      }}
                      customOption={
                        <DatePicker
                          placeholder="MM/DD/YYYY"
                          name="overrideStartDate"
                          defaultValue={
                            formik.values?.overrideStartDate
                              ? toDateWithoutTimeZoneV2(formik.values.overrideStartDate)
                              : undefined
                          }
                          onChange={async (value: any) => {
                            if (value) {
                              formik.setFieldValue('overrideStartDateSelect', value)
                              const result = await sendIsPurchaseDateValidForEmployee(() =>
                                isPremiumPurchaseDateValidForEmployee(
                                  initialValues.planPeriodId,
                                  initialValues.companyUser.id,
                                  value,
                                ),
                              )
                              setOverrideStartDateValidResponse(result)
                            } else {
                              formik.setFieldValue('overrideStartDateSelect', ReviewerAction.Verified)
                              setOverrideStartDateValidResponse(IsPurchaseDateValidForEmployeeResponse.Correct)
                            }
                          }}
                          id="overrideStartDate"
                          format="MM/DD/YYYY"
                          noStyles
                        />
                      }
                      actionIcon={getActionIcon(formik.values?.overrideStartDateSelect)}
                    />

                    <PremiumFormItemSelect
                      itemDisplayName={'End Date'}
                      itemDisplayValue={parseZoneFormatV2(initialValues.endDate)}
                      inputName={'overrideEndDateSelect'}
                      inputValue={
                        formik.values?.overrideEndDateSelect != undefined
                          ? [formik.values?.overrideEndDateSelect]
                          : undefined
                      }
                      disabled={!initialValues.canReview}
                      actionComponentError={
                        overrideEndDateValidResponse !== IsPurchaseDateValidForEmployeeResponse.Correct
                          ? `Value is outside user's eligibility dates.`
                          : null
                      }
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault()
                          if (formik.values?.overrideEndDate) {
                            formik.setFieldValue('overrideEndDateSelect', formik.values?.overrideEndDate)
                          }
                        }
                      }}
                      customOption={
                        <DatePicker
                          placeholder="MM/DD/YYYY"
                          name="overrideEndDate"
                          defaultValue={
                            formik.values?.overrideEndDate
                              ? toDateWithoutTimeZoneV2(formik.values.overrideEndDate)
                              : undefined
                          }
                          onChange={async (value: any) => {
                            if (value) {
                              formik.setFieldValue('overrideEndDateSelect', value)
                              const result = await sendIsPurchaseDateValidForEmployee(() =>
                                isPremiumPurchaseDateValidForEmployee(
                                  initialValues.planPeriodId,
                                  initialValues.companyUser.id,
                                  value,
                                ),
                              )
                              setOverrideEndDateValidResponse(result)
                            } else {
                              formik.setFieldValue('overrideEndDateSelect', ReviewerAction.Verified)
                              setOverrideEndDateValidResponse(IsPurchaseDateValidForEmployeeResponse.Correct)
                            }
                          }}
                          id="overrideEndDate"
                          format="MM/DD/YYYY"
                          noStyles
                        />
                      }
                      actionIcon={getActionIcon(formik.values?.overrideEndDateSelect)}
                    />

                    <PremiumFormItemSelect
                      itemDisplayName={translate('doc-review-dashboard.expenses.review-expense-form.amount-submitted')}
                      itemDisplayValue={`$${initialValues.requestedAmount}`}
                      inputName={'overrideAmountSelect'}
                      inputValue={
                        formik.values?.overrideAmountSelect != undefined
                          ? [formik.values?.overrideAmountSelect]
                          : undefined
                      }
                      disabled={!initialValues.canReview}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault()
                          if (formik.values?.overrideAmount) {
                            formik.setFieldValue('overrideAmountSelect', formik.values?.overrideAmount)
                          }
                        }
                      }}
                      customOption={
                        <InputNumber
                          name="overrideAmount"
                          min={0.01}
                          onChange={(value) => {
                            formik.setFieldValue('overrideAmountSelect', value ?? ReviewerAction.Verified)
                          }}
                          formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        />
                      }
                      actionIcon={getActionIcon(formik.values?.overrideAmountSelect)}
                    />

                    <PremiumFormItemSelect
                      itemDisplayName={translate('doc-review-dashboard.expenses.review-expense-form.expense-type')}
                      itemDisplayValue={initialValues.expenseType.name}
                      inputName={'overrideExpenseTypeIdSelect'}
                      inputValue={
                        formik.values?.overrideExpenseTypeIdSelect != undefined
                          ? [formik.values?.overrideExpenseTypeIdSelect]
                          : undefined
                      }
                      disabled={!initialValues.canReview}
                      onChange={(value) => {
                        formik.setFieldValue('messageToEmployeeTemplateSelect', '')
                        formik.setFieldValue('messageToEmployee', '')
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault()
                          if (formik.values?.overrideExpenseTypeId) {
                            const found = initialValues.availableExpenseTypes.find(
                              (x) => x.id == formik.values?.overrideExpenseTypeId,
                            )
                            formik.setFieldValue('overrideExpenseTypeIdSelect', found?.name)
                          }
                        }
                      }}
                      customOption={
                        <DefaultSelect
                          showSearch
                          name="overrideExpenseTypeId"
                          placeholder="Search"
                          filterOption={filterByTooltipValue}
                          onChange={(value) => {
                            const found = initialValues.availableExpenseTypes.find((x) => x.id == value)
                            formik.setFieldValue('overrideExpenseTypeIdSelect', found?.name)
                            if (found?.id == -2) {
                              const message = premium.availableCannedMessages.find((c) => c.title === 'Duplicate')
                              formik.setFieldValue('messageToEmployeeTemplateSelect', message.id)
                              formik.setFieldValue('messageToEmployee', getMessageToEmployeeValue(formik))
                            } else {
                              formik.setFieldValue('messageToEmployeeTemplateSelect', '')
                              formik.setFieldValue('messageToEmployee', '')
                            }
                          }}
                        >
                          {initialValues.availableExpenseTypes.map((ae) => {
                            return (
                              <Option key={ae.id} value={ae.id}>
                                <Tooltip title={ae.name}>
                                  <span>{ae.name}</span>
                                </Tooltip>
                              </Option>
                            )
                          })}
                        </DefaultSelect>
                      }
                      customOptionClassname={'w-100'}
                      actionIcon={getActionIcon(formik?.values.overrideExpenseTypeIdSelect)}
                    />

                    <PremiumFormItemSelect
                      itemDisplayName={'Frequency'}
                      itemDisplayValue={initialValues.frequency.name}
                      inputName={'overrideFrequencySelect'}
                      inputValue={
                        formik.values?.overrideFrequencySelect != undefined
                          ? [formik.values?.overrideFrequencySelect]
                          : undefined
                      }
                      disabled={!initialValues.canReview}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault()
                          if (formik.values?.overrideFrequency) {
                            const found = initialValues.availableFrequencies.find(
                              (x) => x.id == formik.values?.overrideFrequency,
                            )
                            formik.setFieldValue('overrideFrequencySelect', found?.name)
                          }
                        }
                      }}
                      customOption={
                        <DefaultSelect
                          showSearch
                          name="overrideFrequency"
                          placeholder="Search"
                          filterOption={filterByTooltipValue}
                          onChange={(value) => {
                            const found = initialValues.availableFrequencies.find((x) => x.id == value)
                            formik.setFieldValue('overrideFrequencySelect', found?.name)
                          }}
                        >
                          {initialValues.availableFrequencies.map((ae) => {
                            return (
                              <Option key={ae.id} value={ae.id}>
                                <Tooltip title={ae.name}>
                                  <span>{ae.name}</span>
                                </Tooltip>
                              </Option>
                            )
                          })}
                        </DefaultSelect>
                      }
                      actionIcon={getActionIcon(formik?.values.overrideFrequencySelect)}
                    />

                    <PremiumFormItem
                      itemName={translate('doc-review-dashboard.expenses.review-expense-form.documents-submitted')}
                      itemValue={<DocumentComponent documents={initialValues.attachments} />}
                    />

                    <PremiumFormItem
                      itemName={translate('doc-review-dashboard.expenses.review-expense-form.notes-from-user')}
                      itemValue={initialValues.description}
                    />

                    <PremiumFormItem
                      itemName={translate('doc-review-dashboard.expenses.review-expense-form.message-to-employee')}
                      actionComponent={
                        <div>
                          <Row justify="end" gutter={[16, 16]}>
                            <Col span={15}>
                              <DefaultSelect
                                showSearch
                                name="messageToEmployeeTemplateSelect"
                                placeholder="Add template"
                                filterOption={filterByValue}
                                disabled={!initialValues.canReview}
                                className="w-100 mb-8"
                              >
                                {initialValues.availableCannedMessages.map((cannedMessage) => {
                                  return (
                                    <Option key={cannedMessage.id} value={cannedMessage.id}>
                                      {cannedMessage.title}
                                    </Option>
                                  )
                                })}
                              </DefaultSelect>
                            </Col>
                          </Row>
                          <HtmlEditor name="messageToEmployee" value={getMessageToEmployeeValue(formik)} />
                        </div>
                      }
                    />

                    <PremiumFormItem
                      itemName={translate('doc-review-dashboard.expenses.review-expense-form.internal-notes')}
                      actionComponent={
                        <FormikTextAreaField name="messageToReviewer" disabled={!initialValues.canReview} />
                      }
                    />

                    <Row>
                      <Col>
                        <div className="m16-24">
                          <Button
                            onClick={() => saveReview(formik)}
                            type="primary"
                            disabled={
                              !formValidator(translate).isValidSync(formik.values) ||
                              isReviewPremiumCommandLoading ||
                              !initialValues.canReview
                            }
                            className=" mr-8"
                            loading={isReviewPremiumCommandLoading}
                          >
                            {translate('general.save')}
                          </Button>

                          <Button
                            onClick={() => skipExpense(formik)}
                            type="default"
                            disabled={isSkipRecurringExpenseFoReviewCommandLoading || !initialValues.canReview}
                            loading={isSkipRecurringExpenseFoReviewCommandLoading}
                          >
                            {translate('general.skip')}
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </form>
                )}
              </Formik>
            </Col>

            <Col span={7}>
              <PriorPremiums premiumId={premium.recurringExpenseId} onPriorPremiumClick={props.onPriorPremiumClick} />
            </Col>
          </Row>
        </Card>
      )}
    </Skeleton>
  )
}

const ValidationRelatedToDuplicateExpenseType = (translate: any): MixedSchema<any> => {
  return Yup.mixed()
    .nullable()
    .when(['overrideExpenseTypeId', 'overrideExpenseTypeIdSelect'], {
      is: (overrideExpenseTypeId: number, overrideExpenseTypeIdSelect: string) =>
        overrideExpenseTypeId === -2 && overrideExpenseTypeIdSelect === 'Duplicate',
      then: Yup.mixed().notRequired(),
      otherwise: Yup.mixed().required(translate('general.required')),
    })
}
const formValidator = (translate: any) =>
  Yup.object({
    overrideStartDateSelect: ValidationRelatedToDuplicateExpenseType(translate),

    overrideEndDateSelect: ValidationRelatedToDuplicateExpenseType(translate),

    overrideAmountSelect: ValidationRelatedToDuplicateExpenseType(translate),

    overrideAmount: Yup.number().nullable().min(0.01).notRequired(),

    overrideExpenseTypeIdSelect: Yup.mixed()
      .nullable()
      .when('overrideExpenseTypeId', {
        is: (overrideExpenseTypeId: number) => overrideExpenseTypeId == undefined,
        then: Yup.number()
          .required(translate('general.required'))
          .oneOf([ReviewerAction.Missing, ReviewerAction.Verified]),
      }),

    overrideFrequencySelect: ValidationRelatedToDuplicateExpenseType(translate),

    overrideNameSelect: ValidationRelatedToDuplicateExpenseType(translate),
    overrideName: Yup.string().nullable().notRequired(),

    messageToEmployee: Yup.string()
      .nullable()
      .when(
        [
          'overrideStartDateSelect',
          'overrideEndDateSelect',
          'overrideAmountSelect',
          'overrideExpenseTypeIdSelect',
          'overrideFrequencySelect',
          'overrideNameSelect',
        ],
        {
          is: (
            overrideStartDateSelect,
            overrideEndDateSelect,
            overrideAmountSelect,
            overrideExpenseTypeIdSelect,
            overrideFrequencySelect,
            overrideNameSelect,
          ) => {
            return (
              overrideStartDateSelect === ReviewerAction.Missing ||
              overrideEndDateSelect === ReviewerAction.Missing ||
              overrideAmountSelect === ReviewerAction.Missing ||
              overrideExpenseTypeIdSelect === ReviewerAction.Missing ||
              overrideFrequencySelect === ReviewerAction.Missing ||
              overrideNameSelect === ReviewerAction.Missing
            )
          },
          then: Yup.string().required(translate('general.required')),
        },
      ),
  })

type FormData = RecurringExpenseDetailsForReview & {
  overrideStartDateSelect?: string | number
  overrideStartDate?: string
  overrideAmountSelect?: string | number
  overrideAmount?: number
  overrideExpenseTypeIdSelect?: string | number
  overrideExpenseTypeId?: number
  overrideFrequencySelect?: string | number
  overrideFrequency?: number
  overrideNameSelect?: string | number
  overrideName?: string
  messageToReviewer?: string
  messageToEmployee?: string
  messageToEmployeeTemplateSelect?: string
  overrideEndDate?: string
  overrideEndDateSelect?: string | number
}

const getRecurringReviewSectionStatus = (value: any): RecurringReviewSectionStatus => {
  if (value === ReviewerAction.Verified) return RecurringReviewSectionStatus.Verified

  if (value === ReviewerAction.Missing) return RecurringReviewSectionStatus.Missing

  return RecurringReviewSectionStatus.Override
}

const getActionIcon = (value?: any): ActionIcon | null => {
  if (value === undefined || value === null || value === '') return null

  if (value === ReviewerAction.Verified) return ActionIcon.VerifiedActionIcon

  if (value === ReviewerAction.Missing) return ActionIcon.MissingActionIcon

  return ActionIcon.OverrideActionIcon
}

export default ReviewPremiumForm

type PremiumFormItemProps = {
  itemName: string
  itemValue?: any
  showEmptyItemValue?: boolean
  actionComponent?: ReactNode
  actionComponentError?: string
  actionIcon?: ActionIcon | null
}

const PremiumFormItem: React.FC<PremiumFormItemProps> = ({
  itemName,
  itemValue,
  actionComponentError,
  actionComponent,
  actionIcon,
}) => {
  const iconClassName = classnames({
    'color-green-6': actionIcon === ActionIcon.VerifiedActionIcon,
    'far fa-times-circle color-red-6': actionIcon === ActionIcon.MissingActionIcon,
    'color-yellow-6': actionIcon === ActionIcon.OverrideActionIcon,
    'ml-8': true,
  })

  const showFullRow = itemName === 'Message to employee' || itemName === 'Internal notes'

  return (
    <Row className="review_form_item">
      <Col span={8}>
        <div>
          <span>{itemName}</span>
        </div>
      </Col>
      {itemValue && (
        <Col span={actionComponent ? 6 : 16}>
          <div>
            <span>{itemValue}</span>
          </div>
        </Col>
      )}
      {actionComponent && (
        <Col span={itemValue || itemValue === '' ? 10 : 16}>
          <Row align="middle">
            <Col span={(showFullRow && 24) || 20}>{actionComponent}</Col>
            {actionIcon && (
              <Col span={4} style={{ height: '100%' }}>
                <div>
                  <Icon type={`far fa-${actionIcon}`} fontAwesome className={iconClassName} />
                </div>
              </Col>
            )}
          </Row>
          {actionComponentError && <span className={'color-red-6'}>{actionComponentError}</span>}
        </Col>
      )}
    </Row>
  )
}

type PremiumFormItemSelectProps = {
  itemDisplayName: string
  itemDisplayValue?: any
  inputName: string
  onChange?: (value: any) => void
  inputValue?: any
  disabled?: boolean
  customOption?: ReactNode
  customOptionClassname?: string
  actionIcon?: ActionIcon | null
  actionComponentError?: string
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void
}

const PremiumFormItemSelect: React.FC<PremiumFormItemSelectProps> = (props) => {
  const { t: translate } = useTranslation()

  return (
    <PremiumFormItem
      itemName={props.itemDisplayName}
      itemValue={props.itemDisplayValue}
      actionComponentError={props.actionComponentError}
      actionComponent={
        <FormikSelectWithChildrenField
          name={props.inputName}
          value={props.inputValue}
          placeholder="Select"
          dropdownMatchSelectWidth={false}
          disabled={props.disabled}
          onKeyDown={props.onKeyDown}
          customOptionPlaceholder={translate('doc-review-dashboard.expenses.review-expense-form.reviwer-override')}
          customOption={props.customOption}
          customOptionClassname={props.customOptionClassname}
          onChange={(value) => {
            if (props.onChange) {
              props.onChange(value)
            }
          }}
        >
          {reviewerActions.map((action) => (
            <Option key={action} value={action}>
              {reviewActionTitleMap.get(action)}
            </Option>
          ))}
        </FormikSelectWithChildrenField>
      }
      actionIcon={props.actionIcon}
    />
  )
}

const reviewerActions = [ReviewerAction.Verified, ReviewerAction.Missing]
const reviewActionTitleMap = new Map<ReviewerAction, string>([
  [ReviewerAction.Verified, 'Verified'],
  [ReviewerAction.Missing, 'Missing'],
  [ReviewerAction.Override, 'Override'],
])
